import React, { useState, useEffect } from 'react';
import {
  Building2, Plus, Trash2, Edit, Save, X, Search, Upload, Star,
  Eye, EyeOff, Users, ExternalLink, RefreshCw, Briefcase, Crown, ArrowUp, ArrowDown
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GROUP_TYPES = ['Hospitality', 'Food & Beverage', 'Retail', 'Education', 'Café', 'Entertainment', 'Other'];
const ACCENT_PRESETS = [
  { label: 'Cyan', color: '#00E5FF' },
  { label: 'Amber', color: '#F59E0B' },
  { label: 'Violet', color: '#8A2BE2' },
  { label: 'Emerald', color: '#10B981' },
  { label: 'Rose', color: '#EF4444' },
  { label: 'Blue', color: '#3B82F6' },
];

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${BACKEND_URL}${path.startsWith('/api/') ? '' : '/api'}${path.startsWith('/') ? '' : '/'}${path}`;
};

const emptyForm = {
  name: '', type: 'Hospitality', tagline: '', short_description: '', full_description: '',
  image_url: '', roblox_link: '', roblox_group_id: '', featured: false, is_hiring: false,
  display_order: 0, is_active: true, accent_color: '#00E5FF'
};

const PortfolioManagementPage = ({ getAuthHeaders, currentUser }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState(emptyForm);
  const [liveStats, setLiveStats] = useState({});
  const [refreshingStats, setRefreshingStats] = useState(false);

  const canManage = currentUser?.role === 'superadmin';

  useEffect(() => { if (canManage) fetchGroups(); }, [canManage]);

  const fetchGroups = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const r = await axios.get(`${BACKEND_URL}/api/portfolio/admin`, { headers });
      setGroups(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load groups');
    } finally { setLoading(false); }
  };

  const refreshLiveStats = async () => {
    setRefreshingStats(true);
    const stats = {};
    await Promise.all(groups.filter(g => g.roblox_group_id).map(async (g) => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/portfolio/roblox-stats/${g.roblox_group_id}`);
        stats[g.id] = r.data;
      } catch { stats[g.id] = { error: true }; }
    }));
    setLiveStats(stats);
    setRefreshingStats(false);
    toast.success('Live stats refreshed');
  };

  useEffect(() => { if (groups.length > 0) refreshLiveStats(); /* eslint-disable-next-line */ }, [groups.length]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Image files only'); return; }
    setUploading(true);
    try {
      const headers = getAuthHeaders();
      const fd = new FormData();
      fd.append('image', file);
      const r = await axios.post(`${BACKEND_URL}/api/portfolio/admin/upload-image`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      setFormData(fd2 => ({ ...fd2, image_url: r.data.image_url }));
      toast.success('Image uploaded');
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.short_description) {
      toast.error('Name and short description required'); return;
    }
    try {
      const headers = getAuthHeaders();
      const payload = {
        ...formData,
        roblox_group_id: formData.roblox_group_id ? parseInt(formData.roblox_group_id, 10) : null,
        display_order: parseInt(formData.display_order || 0, 10),
      };
      if (editing) {
        await axios.put(`${BACKEND_URL}/api/portfolio/admin/${editing.id}`, payload, { headers });
        toast.success('Group updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/portfolio/admin`, payload, { headers });
        toast.success('Group created');
      }
      setShowForm(false); setEditing(null); setFormData(emptyForm); fetchGroups();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const handleEdit = (g) => {
    setEditing(g);
    setFormData({
      name: g.name || '', type: g.type || 'Hospitality', tagline: g.tagline || '',
      short_description: g.short_description || '', full_description: g.full_description || '',
      image_url: g.image_url || '', roblox_link: g.roblox_link || '',
      roblox_group_id: g.roblox_group_id || '', featured: !!g.featured, is_hiring: !!g.is_hiring,
      display_order: g.display_order || 0, is_active: g.is_active !== false,
      accent_color: g.accent_color || '#00E5FF'
    });
    setShowForm(true);
  };

  const handleDelete = async (g) => {
    if (!window.confirm(`Delete "${g.name}"?`)) return;
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${BACKEND_URL}/api/portfolio/admin/${g.id}`, { headers });
      toast.success('Deleted'); fetchGroups();
    } catch { toast.error('Delete failed'); }
  };

  const toggleActive = async (g) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${BACKEND_URL}/api/portfolio/admin/${g.id}`, { is_active: !g.is_active }, { headers });
      toast.success(g.is_active ? 'Hidden from public' : 'Visible on homepage'); fetchGroups();
    } catch { toast.error('Update failed'); }
  };

  const toggleFeatured = async (g) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${BACKEND_URL}/api/portfolio/admin/${g.id}`, { featured: !g.featured }, { headers });
      toast.success('Featured toggled'); fetchGroups();
    } catch { toast.error('Update failed'); }
  };

  const moveOrder = async (g, dir) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${BACKEND_URL}/api/portfolio/admin/${g.id}`, {
        display_order: (g.display_order || 0) + dir
      }, { headers });
      fetchGroups();
    } catch { toast.error('Update failed'); }
  };

  if (!canManage) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="portfolio-management-denied">
        <div className="text-center">
          <Crown className="w-14 h-14 text-amber-400 mx-auto mb-4" />
          <p className="text-slate-400">Superadmin or Executive access required</p>
        </div>
      </div>
    );
  }

  const filtered = groups.filter(g =>
    g.name.toLowerCase().includes(search.toLowerCase()) ||
    (g.type || '').toLowerCase().includes(search.toLowerCase())
  );

  const inputCls = "w-full px-4 py-3 bg-slate-900/80 border border-slate-700/50 rounded-xl text-white focus:outline-none focus:border-cyan-500/50 placeholder-slate-600 text-sm";
  const labelCls = "block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider";

  return (
    <div className="space-y-6" data-testid="portfolio-management-page">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-slate-900 via-cyan-950/30 to-slate-900 border border-cyan-500/20 p-6">
        <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="relative flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500/30 to-violet-600/30 flex items-center justify-center border border-cyan-500/20">
              <Building2 className="w-7 h-7 text-cyan-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Portfolio Management</h1>
              <p className="text-slate-400 text-sm">Control the "Our Portfolio" section shown on the homepage</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={refreshLiveStats} disabled={refreshingStats}
              data-testid="refresh-stats-btn"
              className="bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700">
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshingStats ? 'animate-spin' : ''}`} />
              Refresh Live Stats
            </Button>
            <Button onClick={() => { setFormData(emptyForm); setEditing(null); setShowForm(true); }}
              data-testid="add-group-btn"
              className="bg-gradient-to-r from-cyan-500 to-violet-600 hover:from-cyan-600 hover:to-violet-700 text-white shadow-lg shadow-cyan-500/20">
              <Plus className="w-4 h-4 mr-2" /> Add Group
            </Button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Groups', value: groups.length, icon: Building2, color: 'cyan' },
          { label: 'Featured', value: groups.filter(g => g.featured).length, icon: Star, color: 'yellow' },
          { label: 'Hiring', value: groups.filter(g => g.is_hiring).length, icon: Briefcase, color: 'emerald' },
          { label: 'Active', value: groups.filter(g => g.is_active).length, icon: Eye, color: 'violet' },
        ].map((s, i) => (
          <div key={i} className="bg-slate-900/60 border border-slate-800/50 rounded-xl p-4 flex items-center gap-4">
            <div className={`w-11 h-11 rounded-xl bg-${s.color}-500/20 flex items-center justify-center`}>
              <s.icon className={`w-5 h-5 text-${s.color}-400`} />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{s.value}</p>
              <p className="text-slate-500 text-xs">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search groups..." data-testid="portfolio-search-input"
          className="w-full pl-10 pr-4 py-2.5 bg-slate-900/60 border border-slate-700/50 rounded-xl text-white text-sm placeholder-slate-500 focus:outline-none focus:border-cyan-500/50" />
      </div>

      {/* Groups list */}
      {loading ? (
        <div className="text-center py-16 text-slate-500">
          <div className="w-10 h-10 border-3 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin mx-auto mb-3" />
          Loading...
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-slate-900/40 border border-slate-800/50 rounded-2xl">
          <Building2 className="w-14 h-14 text-slate-700 mx-auto mb-3" />
          <p className="text-slate-500 mb-4">No groups yet</p>
          <Button onClick={() => { setFormData(emptyForm); setShowForm(true); }}
            className="bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 border border-cyan-500/30">
            <Plus className="w-4 h-4 mr-2" />Add First Group
          </Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {filtered.map(g => {
            const stats = liveStats[g.id];
            return (
              <div key={g.id} data-testid={`portfolio-card-${g.id}`}
                className={`group bg-slate-900/60 border rounded-2xl overflow-hidden transition-all ${g.is_active ? 'border-slate-800/50 hover:border-cyan-500/30' : 'border-slate-800/30 opacity-60'}`}>
                {/* Image banner */}
                <div className="relative h-32 overflow-hidden bg-slate-800">
                  {g.image_url && (
                    <img src={getImageUrl(g.image_url)} alt={g.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => e.target.style.display = 'none'} />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent" />
                  <div className="absolute top-2 left-2 flex gap-1.5">
                    <Badge className="bg-black/60 text-white border-none text-[10px] backdrop-blur-sm">{g.type}</Badge>
                    {g.featured && <Badge className="bg-yellow-500/90 text-black border-none text-[10px]"><Star className="w-2.5 h-2.5 mr-0.5 fill-current" />Featured</Badge>}
                    {g.is_hiring && <Badge className="bg-emerald-500/90 text-white border-none text-[10px]"><Briefcase className="w-2.5 h-2.5 mr-0.5" />Hiring</Badge>}
                  </div>
                  <div className="absolute bottom-2 left-3 right-3 flex items-end justify-between">
                    <h3 className="text-white font-semibold drop-shadow-lg">{g.name}</h3>
                    {stats?.member_count != null && (
                      <Badge className="bg-cyan-500/90 text-white border-none text-[10px] flex items-center gap-1">
                        <Users className="w-2.5 h-2.5" />{stats.member_count.toLocaleString()}
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="p-4">
                  {g.tagline && <p className="text-cyan-400/70 text-xs italic mb-1.5">{g.tagline}</p>}
                  <p className="text-slate-400 text-sm line-clamp-2 mb-3">{g.short_description}</p>

                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1 text-[10px] text-slate-500">
                      <span>Order: {g.display_order || 0}</span>
                      <button onClick={() => moveOrder(g, -1)} className="p-1 hover:text-cyan-400" title="Move up">
                        <ArrowUp className="w-3 h-3" />
                      </button>
                      <button onClick={() => moveOrder(g, 1)} className="p-1 hover:text-cyan-400" title="Move down">
                        <ArrowDown className="w-3 h-3" />
                      </button>
                    </div>
                    <div className="flex items-center gap-1">
                      {g.roblox_link && (
                        <a href={g.roblox_link} target="_blank" rel="noopener noreferrer"
                          className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-cyan-400" title="Open Roblox">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                      <button onClick={() => toggleFeatured(g)} title="Toggle featured"
                        className={`p-1.5 rounded-lg hover:bg-slate-800 ${g.featured ? 'text-yellow-400' : 'text-slate-500'}`}>
                        <Star className={`w-4 h-4 ${g.featured ? 'fill-current' : ''}`} />
                      </button>
                      <button onClick={() => toggleActive(g)} title="Toggle visible"
                        className={`p-1.5 rounded-lg hover:bg-slate-800 ${g.is_active ? 'text-emerald-400' : 'text-slate-500'}`}>
                        {g.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                      <button onClick={() => handleEdit(g)} data-testid={`edit-group-${g.id}`}
                        className="p-1.5 rounded-lg hover:bg-slate-800 text-cyan-400"><Edit className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(g)} data-testid={`delete-group-${g.id}`}
                        className="p-1.5 rounded-lg hover:bg-red-500/20 text-red-400"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={(o) => { if (!o) { setShowForm(false); setEditing(null); setFormData(emptyForm); } }}>
        <DialogContent className="bg-slate-950 border-cyan-500/20 text-white max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="portfolio-form-dialog">
          <DialogHeader>
            <DialogTitle className="text-cyan-400 flex items-center gap-2">
              <Building2 className="w-5 h-5" />{editing ? 'Edit Group' : 'Add New Group'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className={labelCls}>Name *</label>
                <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })}
                  className={inputCls} data-testid="group-name-input" placeholder="Greggs PLC" /></div>
              <div><label className={labelCls}>Type</label>
                <select value={formData.type} onChange={e => setFormData({ ...formData, type: e.target.value })} className={inputCls}>
                  {GROUP_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div><label className={labelCls}>Tagline</label>
              <input type="text" value={formData.tagline} onChange={e => setFormData({ ...formData, tagline: e.target.value })}
                className={inputCls} placeholder="Short one-liner" /></div>
            <div><label className={labelCls}>Short Description *</label>
              <textarea value={formData.short_description} onChange={e => setFormData({ ...formData, short_description: e.target.value })}
                rows={2} className={inputCls + ' resize-none'} data-testid="group-short-desc-input" /></div>
            <div><label className={labelCls}>Full Description</label>
              <textarea value={formData.full_description} onChange={e => setFormData({ ...formData, full_description: e.target.value })}
                rows={4} className={inputCls + ' resize-none'} /></div>

            {/* Image */}
            <div>
              <label className={labelCls}>Banner Image</label>
              <div className="flex items-center gap-4">
                {formData.image_url && (
                  <div className="w-24 h-16 rounded-lg bg-slate-800 overflow-hidden border border-slate-700">
                    <img src={getImageUrl(formData.image_url)} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                <label className="flex items-center gap-2 px-4 py-2.5 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 rounded-xl cursor-pointer text-sm border border-cyan-500/20">
                  <Upload className="w-4 h-4" />{uploading ? 'Uploading...' : 'Upload'}
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading} />
                </label>
                <input type="url" value={formData.image_url} onChange={e => setFormData({ ...formData, image_url: e.target.value })}
                  className={inputCls + ' flex-1'} placeholder="or paste URL" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div><label className={labelCls}>Roblox Group/Community Link</label>
                <input type="url" value={formData.roblox_link}
                  onChange={e => {
                    const link = e.target.value;
                    const m = link.match(/\/(?:groups|communities)\/(\d+)/);
                    setFormData({ ...formData, roblox_link: link, roblox_group_id: m ? m[1] : formData.roblox_group_id });
                  }}
                  className={inputCls} placeholder="https://www.roblox.com/communities/..." /></div>
              <div><label className={labelCls}>Roblox Group ID (auto)</label>
                <input type="number" value={formData.roblox_group_id}
                  onChange={e => setFormData({ ...formData, roblox_group_id: e.target.value })}
                  className={inputCls} placeholder="e.g. 10341599" /></div>
            </div>

            {/* Accent */}
            <div>
              <label className={labelCls}>Accent Color</label>
              <div className="flex items-center gap-2 flex-wrap">
                {ACCENT_PRESETS.map(p => (
                  <button key={p.color} onClick={() => setFormData({ ...formData, accent_color: p.color })}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${formData.accent_color === p.color ? 'border-white scale-110' : 'border-slate-700'}`}
                    style={{ background: p.color }} title={p.label} />
                ))}
                <input type="color" value={formData.accent_color}
                  onChange={e => setFormData({ ...formData, accent_color: e.target.value })}
                  className="w-10 h-10 rounded-lg bg-transparent border border-slate-700 cursor-pointer" />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div><label className={labelCls}>Display Order</label>
                <input type="number" value={formData.display_order} onChange={e => setFormData({ ...formData, display_order: e.target.value })}
                  className={inputCls} /></div>
              <div className="flex items-end gap-4 pb-1">
                {[
                  { key: 'featured', label: 'Featured' },
                  { key: 'is_hiring', label: 'Hiring' },
                  { key: 'is_active', label: 'Active' },
                ].map(t => (
                  <label key={t.key} className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData[t.key]} onChange={e => setFormData({ ...formData, [t.key]: e.target.checked })}
                      className="w-4 h-4 rounded bg-slate-800 border-slate-600 text-cyan-500" />
                    <span className="text-slate-300 text-sm">{t.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-800">
              <Button onClick={() => { setShowForm(false); setEditing(null); setFormData(emptyForm); }}
                variant="outline" className="border-slate-700 text-slate-400">
                <X className="w-4 h-4 mr-2" />Cancel
              </Button>
              <Button onClick={handleSubmit} data-testid="save-group-btn"
                className="bg-gradient-to-r from-cyan-500 to-violet-600 hover:from-cyan-600 hover:to-violet-700 text-white shadow-lg shadow-cyan-500/20">
                <Save className="w-4 h-4 mr-2" />{editing ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PortfolioManagementPage;
